import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type Attachment,
  IntervalType,
  type Project,
} from '../../types/project'
import type { TruckType, WorkOrder } from '../../types/workorder'
import DatePicker from '../DatePicker'
import GooglePlacesSelect from '../GooglePlacesSelect'
import MultiSelect from '../MultiSelect'
import type { Option } from '../MultiSelect'
import NoteAttachments from '../NoteAttachments'
import TimePicker from '../Timepicker'

type GooglePlaceOption = { label: string; value: string }

interface JobDetailsStepProps {
  data: Partial<WorkOrder>
  onUpdate: (data: Partial<WorkOrder>) => void
  project?: Project
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
  // Attachment props
  attachments?: Attachment[]
  pendingAttachments?: File[]
  onPendingAttachmentsChange?: (files: File[]) => void
  onAttachmentUpload?: (files: File[]) => Promise<void>
  onAttachmentDelete?: (id: number) => Promise<void>
}

const INTERVAL_OPTIONS: Option[] = [
  { value: IntervalType.DAILY, label: 'Daily' },
  { value: IntervalType.WEEKLY, label: 'Weekly' },
  { value: IntervalType.BIWEEKLY, label: 'Biweekly' },
  { value: IntervalType.MONTHLY, label: 'Monthly' },
]

const TRUCK_TYPE_OPTIONS: Option[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'super', label: 'Super' },
  { value: 'winter', label: 'Winter' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'side', label: 'Side' },
  { value: 'haul', label: 'Haul' },
  { value: 'semi_end', label: 'Semi End' },
  { value: 'semi_bottom', label: 'Semi Bottom' },
]

const JobDetailsStep: React.FC<JobDetailsStepProps> = ({
  data,
  onUpdate,
  readOnly = false,
  project,
  onValidationChange,
  showErrors = false,
  attachments = [],
  pendingAttachments = [],
  onPendingAttachmentsChange,
  onAttachmentUpload,
  onAttachmentDelete,
}) => {
  const initialized = useRef(false)
  const prevIsValidRef = useRef<boolean | null>(null)
  const onValidationChangeRef = useRef(onValidationChange)

  // Keep the callback ref updated
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange
  }, [onValidationChange])

  const [pickupAddress, setPickupAddress] = useState<GooglePlaceOption | null>(
    data.pickup_address
      ? { label: data.pickup_address, value: data.pickup_address }
      : project?.pickup_address
        ? { label: project.pickup_address, value: project.pickup_address }
        : null,
  )

  // Validation: Check if all required fields are filled
  useEffect(() => {
    const isValid = Boolean(
      data.pickup_address &&
        data.pickup_address.trim() !== '' &&
        data.dropoff_address &&
        data.dropoff_address.trim() !== '' &&
        data.material &&
        data.material.trim() !== '' &&
        data.trucks_number &&
        data.trucks_number.trim() !== '' &&
        data.truck_types &&
        data.truck_types.length > 0 &&
        data.starting_date &&
        data.starting_date.trim() !== '' &&
        data.completion_date &&
        data.completion_date.trim() !== '',
    )

    // Only call the callback if validity actually changed
    if (prevIsValidRef.current !== isValid) {
      prevIsValidRef.current = isValid
      if (onValidationChangeRef.current) {
        onValidationChangeRef.current(isValid)
      }
    }
  }, [
    data.pickup_address,
    data.dropoff_address,
    data.material,
    data.trucks_number,
    data.truck_types,
    data.starting_date,
    data.completion_date,
  ])
  const [dropoffAddress, setDropoffAddress] =
    useState<GooglePlaceOption | null>(
      data.dropoff_address
        ? { label: data.dropoff_address, value: data.dropoff_address }
        : project?.dropoff_address
          ? { label: project.dropoff_address, value: project.dropoff_address }
          : null,
    )
  const [thirdPartyLocation, setThirdPartyLocation] =
    useState<GooglePlaceOption | null>(
      data.third_party_location
        ? { label: data.third_party_location, value: data.third_party_location }
        : project?.third_party_location
          ? {
              label: project.third_party_location,
              value: project.third_party_location,
            }
          : null,
    )

  // Date and time state
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    data.starting_date ? new Date(data.starting_date) : null,
  )
  const [selectedTime, setSelectedTime] = useState<string | null>(
    data.starting_date ? formatTimeFromDateTime(data.starting_date) : null,
  )
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  // Completion date state
  const [selectedCompletionDate, setSelectedCompletionDate] =
    useState<Date | null>(
      data.completion_date ? new Date(data.completion_date) : null,
    )
  const [isCompletionDatePickerOpen, setIsCompletionDatePickerOpen] =
    useState(false)

  // Helper function to format time from datetime string
  function formatTimeFromDateTime(dateTimeString: string): string {
    const date = new Date(dateTimeString)
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const period = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`
  }

  // Helper function to combine date and time into ISO string
  const combineDateTime = useCallback(
    (date: Date | null, time: string | null): string | null => {
      if (!date) return null

      if (!time) {
        // Default to 8:00 AM if no time selected
        const combined = new Date(date)
        combined.setHours(8, 0, 0, 0)
        return combined.toISOString()
      }

      const [timePart, period] = time.split(' ')
      const [hourStr, minuteStr] = timePart.split(':')
      let hour = Number.parseInt(hourStr)
      const minute = Number.parseInt(minuteStr)

      if (period === 'PM' && hour !== 12) {
        hour += 12
      } else if (period === 'AM' && hour === 12) {
        hour = 0
      }

      const combined = new Date(date)
      combined.setHours(hour, minute, 0, 0)
      return combined.toISOString()
    },
    [],
  )

  // Update starting_date when date or time changes
  useEffect(() => {
    const combinedDateTime = combineDateTime(selectedDate, selectedTime)
    if (combinedDateTime !== data.starting_date) {
      // If no date is selected, default to current date/time
      const finalDateTime = combinedDateTime || new Date().toISOString()
      onUpdate({ starting_date: finalDateTime })
    }
  }, [
    selectedDate,
    selectedTime,
    data.starting_date,
    onUpdate,
    combineDateTime,
  ])

  // Update completion_date when completion date changes
  useEffect(() => {
    if (selectedCompletionDate) {
      const completionDateTime = selectedCompletionDate.toISOString()
      if (completionDateTime !== data.completion_date) {
        onUpdate({ completion_date: completionDateTime })
      }
    } else if (data.completion_date) {
      // Clear completion date if no date selected
      onUpdate({ completion_date: undefined })
    }
  }, [selectedCompletionDate, data.completion_date, onUpdate])

  useEffect(() => {
    // Only populate fields once when component mounts
    if (project && !initialized.current) {
      const updates: Partial<WorkOrder> = {}

      if (!data.material) {
        updates.material = project.material
      }
      if (!data.pickup_address) {
        updates.pickup_address = project.pickup_address
      }
      if (!data.dropoff_address) {
        updates.dropoff_address = project.dropoff_address
      }
      if (!data.third_party_location && project.third_party_location) {
        updates.third_party_location = project.third_party_location
      }

      if (Object.keys(updates).length > 0) {
        onUpdate(updates)
      }

      initialized.current = true
    }
  }, [project, data, onUpdate])

  const getVolumeLabel = (rateType: string | undefined) => {
    switch (rateType?.toLowerCase()) {
      case 'time':
        return 'Time'
      case 'loads':
        return 'Loads'
      default:
        return 'Weight'
    }
  }

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date)
  }

  const handleDateConfirm = () => {
    setIsDatePickerOpen(false)
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
  }

  const handleCompletionDateSelect = (date: Date | null) => {
    setSelectedCompletionDate(date)
  }

  const handleCompletionDateConfirm = () => {
    setIsCompletionDatePickerOpen(false)
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>JOB DETAILS</h3>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Pick Up Location *</label>
        <GooglePlacesSelect
          value={pickupAddress}
          onChange={(value) => {
            setPickupAddress(value)
            onUpdate({ pickup_address: value?.value || '' })
          }}
          isDisabled={readOnly}
          placeholder="Enter pick up address"
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Drop Off Location *</label>
        <GooglePlacesSelect
          value={dropoffAddress}
          onChange={(value) => {
            setDropoffAddress(value)
            onUpdate({ dropoff_address: value?.value || '' })
          }}
          isDisabled={readOnly}
          placeholder="Enter drop off address"
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Third Party Location (optional)
        </label>
        <GooglePlacesSelect
          value={thirdPartyLocation}
          onChange={(value) => {
            setThirdPartyLocation(value)
            onUpdate({ third_party_location: value?.value || '' })
          }}
          isDisabled={readOnly}
          placeholder="Enter third party location"
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Material *</label>
        <input
          type="text"
          value={data.material || ''}
          onChange={(e) => onUpdate({ material: e.target.value })}
          className={css(styles.input)}
          disabled={readOnly}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Number of Trucks *</label>
        <input
          type="text"
          value={data.trucks_number || ''}
          onChange={(e) => onUpdate({ trucks_number: e.target.value })}
          className={css(styles.input)}
          disabled={readOnly}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Truck Types *</label>
        <MultiSelect
          options={TRUCK_TYPE_OPTIONS}
          value={
            data.truck_types?.map((type) => {
              const option = TRUCK_TYPE_OPTIONS.find(
                (opt) => opt.value === type,
              )
              return { value: type, label: option?.label || type }
            }) || []
          }
          onChange={(selected) => {
            if (Array.isArray(selected)) {
              onUpdate({
                truck_types: selected.map((v) => v.value as TruckType),
              })
            } else {
              onUpdate({ truck_types: [] })
            }
          }}
          isDisabled={readOnly}
        />
      </div>

      <div className={css(styles.row)}>
        <div className={css(styles.halfWidth)}>
          <div className={css(styles.formGroup)}>
            <label className={css(styles.label)}>Start Date *</label>
            <input
              type="text"
              value={selectedDate ? selectedDate.toLocaleDateString() : ''}
              onClick={() => !readOnly && setIsDatePickerOpen(true)}
              placeholder="Select start date"
              readOnly
              className={css(styles.input, readOnly && styles.disabled)}
            />
            <DatePicker
              selected={selectedDate}
              onChange={handleDateSelect}
              onSelect={handleDateConfirm}
              minDate={new Date()}
              isOpen={isDatePickerOpen}
              onClose={() => setIsDatePickerOpen(false)}
            />
          </div>
        </div>
        <div className={css(styles.halfWidth)}>
          <div className={css(styles.formGroup)}>
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              label="Start Time"
              disabled={readOnly}
              inModal={true}
              labelStyle={styles.timeLabel}
            />
          </div>
        </div>
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Completion Date *</label>
        <input
          type="text"
          value={
            selectedCompletionDate
              ? selectedCompletionDate.toLocaleDateString()
              : ''
          }
          onClick={() => !readOnly && setIsCompletionDatePickerOpen(true)}
          placeholder="Select completion date"
          readOnly
          className={css(styles.input, readOnly && styles.disabled)}
        />
        <DatePicker
          selected={selectedCompletionDate}
          onChange={handleCompletionDateSelect}
          onSelect={handleCompletionDateConfirm}
          minDate={new Date()}
          isOpen={isCompletionDatePickerOpen}
          onClose={() => setIsCompletionDatePickerOpen(false)}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Status Report Interval</label>
        <MultiSelect
          isMulti={false}
          isDisabled={readOnly}
          placeholder="Select interval"
          options={INTERVAL_OPTIONS}
          value={
            data.status_interval
              ? {
                  value: data.status_interval,
                  label:
                    INTERVAL_OPTIONS.find(
                      (opt) => opt.value === data.status_interval,
                    )?.label || '',
                }
              : undefined
          }
          onChange={(option) => {
            const singleOption =
              option && !Array.isArray(option) ? option : null
            onUpdate({
              status_interval: singleOption?.value || undefined,
            })
          }}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Notes (optional)</label>
        <textarea
          disabled={readOnly}
          value={data.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Enter any additional notes or special instructions"
          className={css(styles.textarea)}
          rows={4}
        />
      </div>

      {/* Attachments */}
      {onPendingAttachmentsChange && (
        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Attachments (optional)</label>
          <NoteAttachments
            attachments={attachments}
            pendingFiles={pendingAttachments}
            onPendingFilesChange={onPendingAttachmentsChange}
            onUpload={onAttachmentUpload}
            onDelete={onAttachmentDelete}
            readOnly={readOnly}
          />
        </div>
      )}

      {/* Validation Error Message */}
      {showErrors &&
        (() => {
          const requiredFields = [
            { value: data.pickup_address, name: 'Pick Up Location' },
            { value: data.dropoff_address, name: 'Drop Off Location' },
            { value: data.material, name: 'Material' },
            { value: data.trucks_number, name: 'Number of Trucks' },
            {
              value: data.truck_types && data.truck_types.length > 0,
              name: 'Truck Types',
            },
            { value: data.starting_date, name: 'Start Date' },
            { value: data.completion_date, name: 'Completion Date' },
          ]

          const missingFields = requiredFields.filter(
            (field) => !field.value || field.value === '',
          )

          if (missingFields.length > 0) {
            return (
              <div className={css(styles.errorMessage)}>
                Please fill in all required fields:{' '}
                {missingFields.map((f) => f.name).join(', ')}
              </div>
            )
          }
          return null
        })()}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '16px',
    '& .google-places-autocomplete__input': {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      fontSize: '14px',
    },
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  timeLabel: {
    display: 'block',
    marginBottom: '0px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '6px',
    fontSize: '14px',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  halfWidth: {
    flex: 1,
  },
  disabled: {
    backgroundColor: '#F3F4F6',
    cursor: 'not-allowed',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '8px',
    fontWeight: 500,
    textAlign: 'center',
    padding: '8px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '6px',
  },
})

export default JobDetailsStep
